import { convertToNumberIfPureNumber } from '../../../../core/utils/string-utils.js';

/**
 * Converts the value to a number if it is not one already and if it is possible.
 * If the converted value is a number and exceeds MAX_SAFE_INTEGER or if it is smaller than MIN_SAFE_INTEGER,
 * the value is converted to bigint.
 */
function convertToSafeNumber(value: string | number): string | number | bigint {
  const convertedValue =
    typeof value === 'number' ? value : convertToNumberIfPureNumber(value);

  const isValueBigInt =
    value !== Infinity &&
    value !== -Infinity &&
    typeof convertedValue === 'number' &&
    (convertedValue > Number.MAX_SAFE_INTEGER ||
      convertedValue < Number.MIN_SAFE_INTEGER);

  return isValueBigInt ? BigInt(value) : convertedValue;
}

/**
 * Compares number values in columns.
 */
export function numberlikeCompare(
  valueA: number | string | bigint,
  valueB: number | string | bigint,
): number {
  let aValue = valueA;
  let bValue = valueB;
  if (typeof valueA === 'string' || typeof valueA === 'number') {
    aValue = convertToSafeNumber(valueA);
  }

  if (typeof valueB === 'string' || typeof valueB === 'number') {
    bValue = convertToSafeNumber(valueB);
  }

  if (
    (typeof aValue === 'number' || typeof aValue === 'bigint') &&
    (typeof bValue === 'number' || typeof bValue === 'bigint')
  ) {
    return compareBasic(aValue, bValue);
  }
  return alphanumericCompare(
    convertToString(aValue).toLowerCase(),
    convertToString(bValue).toLowerCase(),
  );
}

/**
 * Compares alphanumeric values in columns.
 */
export function alphanumericCompare(valueA: string, valueB: string): number {
  const reSplitAlphaNumeric = /([0-9]+)/gm;
  // Split on number groups, but keep the delimiter
  // Then remove falsy values

  const aValue = valueA.split(reSplitAlphaNumeric).filter(Boolean);
  const bValue = valueB.split(reSplitAlphaNumeric).filter(Boolean);

  while (aValue.length && bValue.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const aValueString = aValue.shift()!;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const bValueString = bValue.shift()!;

    const aValueInt = parseInt(aValueString, 10);
    const bValueInt = parseInt(bValueString, 10);

    const combo = [aValueInt, bValueInt].sort();

    // Both are string
    if (isNaN(combo[0])) {
      if (aValueString > bValueString) {
        return 1;
      }
      if (bValueString > aValueString) {
        return -1;
      }
      continue;
    }

    // One is a string, one is a number
    if (isNaN(combo[1])) {
      return isNaN(aValueInt) ? -1 : 1;
    }

    // Both are numbers
    if (aValueInt > bValueInt) {
      return 1;
    }
    if (bValueInt > aValueInt) {
      return -1;
    }
  }

  return aValue.length - bValue.length;
}

/**
 * Compares string values in columns.
 */
export function stringlikeCompare(valueA: string, valueB: string): number {
  return compareBasic(
    convertToString(valueA).toLowerCase(),
    convertToString(valueB).toLowerCase(),
  );
}

/**
 * Compares case-sensitive string values in columns.
 */
export function stringlikeCompareCaseSensitive(
  valueA: string,
  valueB: string,
): number {
  return compareBasic(valueA, valueB);
}

/**
 * Converts the given value into nanoseconds or NaN if the value is not a valid date.
 */
function convertToNanoseconds(value: number | string | Date): bigint | number {
  if (Number.isNaN(value)) {
    return NaN;
  }

  if (typeof value === 'number') {
    return BigInt(value) * 1_000_000n;
  }

  if (value instanceof Date) {
    const valTime = value.getTime();
    if (Number.isNaN(valTime)) {
      return NaN;
    }
    return BigInt(valTime) * 1_000_000n;
  }

  if (typeof value === 'string') {
    return getPreciseTime(value);
  }

  return NaN;
}

/**
 * Compares datetime values in columns.
 * Supported:
 * - number: milliseconds since Unix epoch
 * - string: ISO-like datetime, supports up to 9 fractional digits
 * - Date: JS Date instance
 */
export function datelikeCompare(
  valueA: number | string | Date,
  valueB: number | string | Date,
): number {
  const aValue = convertToNanoseconds(valueA);
  const bValue = convertToNanoseconds(valueB);

  // if the given value is not a valid date, sort it to the end
  if (Number.isNaN(aValue)) {
    return 1;
  }
  if (Number.isNaN(bValue)) {
    return -1;
  }
  return numberlikeCompare(aValue, bValue);
}

/**
 *  Basic sorting used for comparing strings. Much faster than alphanumeric comparison.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compareBasic(valueA: any, valueB: any): number {
  return valueA === valueB ? 0 : valueA > valueB ? 1 : -1;
}

function convertToString(value: unknown) {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

/**
 * Converts the given time value into nanoseconds.
 * @param time - the timestamp to convert in ISO 8601 format.
 * @returns the time in nanoseconds.
 */
function getPreciseTime(time: string): bigint | number {
  const milliseconds = new Date(time).getTime();
  // early exit if the string is not a valid date
  if (isNaN(milliseconds)) {
    return NaN;
  }

  // retrieve the microseconds and nanoseconds part from the fractional seconds
  // and pad the string to ensure it has at least 9 digits (for micro and nanoseconds)
  const regex = /\.(\d{1,9})/;
  const fractionalMatch = regex.exec(time)?.[1] ?? '';
  const microAndNanoseconds = fractionalMatch.padEnd(9, '0').substring(3, 9);

  // JavaScript's Date object only provides precision up to milliseconds.
  // To achieve nanosecond precision, convert milliseconds to nanoseconds and add the remaining precision
  // For positive and negative timestamps, we add the nanoseconds
  // because higher precision values always represent a later point in time
  const msToNs = BigInt(milliseconds) * 1_000_000n;
  const remainingNs = BigInt(microAndNanoseconds);

  return msToNs + remainingNs;
}
